import {
  type ContentObjectView,
  type PostContentObjectDetail,
  type PostContentObjectList,
  isGroupPostContentObjectList,
  isJournalNote,
} from 'models';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canDeleteContentObjectProfile, canEditContentObjectProfile, usePermissions } from 'permissions';
import { Icon, type MenuProps, message } from 'ui';
import { useUser } from 'features/auth';
import { useUnbookmarkObjectMutation } from 'features/bookmark';
import { useReportContentObjectModalConfirm } from 'features/report';
import { EditPostModal } from '../../../edit-post-modal';
import { useDeletePostConfirmModal } from './useDeletePostConfirmModal';

type P = {
  item: PostContentObjectDetail | PostContentObjectList;
  view: ContentObjectView;
};

type UseCreateActionsReturn = {
  actions: MenuProps['items'];
  contexts?: ReactNode[];
  editModal?: ReactNode;
};

const getEditModalType = (item: PostContentObjectDetail | PostContentObjectList) => {
  if (isJournalNote(item)) {
    return 'journal';
  }

  return isGroupPostContentObjectList(item) ? 'announcement' : 'post';
};

const getDeleteConfirmTitle = (item: PostContentObjectDetail | PostContentObjectList) => {
  if (isJournalNote(item)) {
    return 'Are you sure you want to delete this journal?';
  }

  return isGroupPostContentObjectList(item)
    ? 'Are you sure you want to delete this announcement?'
    : 'Are you sure you want to delete this post?';
};

export const useCreateActions = ({ item, view }: P): UseCreateActionsReturn => {
  const [isEditModalOpened, setIsEditModalOpened] = useState(false);
  const { canReportContentObject } = usePermissions();
  const { railsUserData: viewer } = useUser();

  const { t } = useTranslation();
  const { confirm: confirmDeletePost, contextHolder: deleteContentObjectContextHolder } = useDeletePostConfirmModal();

  const { confirm: confirmReportPost, contextHolder: reportContentObjectContextHolder } =
    useReportContentObjectModalConfirm();

  const { mutate: unbookmark } = useUnbookmarkObjectMutation();

  return useMemo(() => {
    if (view === 'bookmark') {
      return {
        actions: [
          {
            icon: <Icon icon="bookmark-line" size="small" />,
            key: 'unbookmark',
            label: t('Unbookmark'),
            onClick() {
              unbookmark(
                { contentObject: item, type: 'rails' },
                {
                  onError() {
                    message.error('Something went wrong.');
                  },
                  onSuccess() {
                    message.success('Successfully unbookmarked');
                  },
                },
              );
            },
          },
        ],
      };
    }

    const actions = [];

    if (canEditContentObjectProfile({ contentObject: item, viewer })) {
      actions.push({
        icon: <Icon icon="pencil-line" size="small" />,
        key: 'update',
        label: t('Edit'),
        onClick() {
          setIsEditModalOpened(true);
        },
      });
    }

    if (canReportContentObject({ contentObject: item })) {
      actions.push({
        icon: <Icon icon="flag-1-line" size="small" />,
        key: 'report',
        label: t('Report'),
        onClick() {
          confirmReportPost({
            id: item.id,
            onSuccess() {
              message.success('Successfully reported');
            },
            title: isGroupPostContentObjectList(item)
              ? 'Are you sure you want to report this announcement?'
              : 'Are you sure you want to report this post?',
          });
        },
      });
    }

    if (canDeleteContentObjectProfile({ contentObject: item, viewer })) {
      actions.push({
        danger: true,
        icon: <Icon color="error" icon="delete-2-line" size="small" />,
        key: 'delete',
        label: t('Delete'),
        onClick() {
          confirmDeletePost({
            item,
            onSuccess() {
              message.success('Successfully deleted');
            },
            title: getDeleteConfirmTitle(item),
          });
        },
      });
    }

    const editModal = (
      <EditPostModal
        isOpened={isEditModalOpened}
        onCancel={() => {
          setIsEditModalOpened(false);
        }}
        postContentObject={item}
        type={getEditModalType(item)}
      />
    );

    return {
      actions,
      contexts: [deleteContentObjectContextHolder, reportContentObjectContextHolder],
      editModal,
    };
  }, [
    view,
    item,
    viewer,
    canReportContentObject,
    isEditModalOpened,
    deleteContentObjectContextHolder,
    reportContentObjectContextHolder,
    t,
    confirmReportPost,
    confirmDeletePost,
    unbookmark,
  ]);
};
